import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import LegacyDashboard from "../components/dashboard/LegacyDashboard";

import ViolationFocus from "../components/dashboard/v2/ViolationFocus";
import CameraStrip from "../components/dashboard/v2/CameraStrip";
import OpenQueue from "../components/dashboard/v2/OpenQueue";
import CalmBoard from "../components/dashboard/v2/CalmBoard";
import WorkProgress from "../components/dashboard/v2/WorkProgress";

import useCameraStatus from "../hooks/useCameraStatus";
import useSafetyOverview from "../hooks/useSafetyOverview";

/*
  대시보드 (2026-10-12 개편).

  이 화면은 «관제»가 아니라 «조치 관리»다. 경쟁 제품(Intenseye·Protex·Voxel)도 같은 구조다.
  그래서 가장 큰 자리에 영상 모음이 아니라 «지금 처리해야 할 위반 한 건»을 둔다.
  그 카메라가 송출 중이면 실시간 영상이, 꺼져 있으면 위반 순간 캡처가 나온다.

  스크롤 없이 한 화면(대표 원칙). 높이를 flex 로 나눠 쓰고 카드가 늘어나지 않게 막는다.

  PPE 라이선스가 없거나 이벤트 열람 권한이 없는 계정은 예전 카드형 대시보드를 그대로 본다
  (민트워치만 쓰는 현장은 «위반»이라는 개념 자체가 없어 이 화면이 빈 껍데기가 된다).
*/

function minutesSince(iso) {
  const t = Date.parse(String(iso || "").replace(" ", "T"));
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
}

export default function DashboardPage(props) {
  const {
    permissionFlags,
    cameras,
    licenseFeatures = {},
    onNavigate,
  } = props;
  // camerasLoading·summary·activePpeCount 는 예전 카드형 화면이 쓰던 값이다.
  // 개편 화면은 숫자를 전부 safety-report·카메라 상태에서 직접 받으므로 쓰지 않는다.
  // (props 는 그대로 받아 LegacyDashboard 로 넘긴다)

  const flags = permissionFlags || {};
  const canSeeEvents = Boolean(flags.canEvents || flags.canEventData);
  const ppeLicensed = Boolean(licenseFeatures?.ppeEnabled);
  const useNewLayout = ppeLicensed && canSeeEvents && Boolean(flags.canLiveView);

  const { statusMap } = useCameraStatus();
  // 위반은 «바로» 떠야 한다. 30초면 관리자가 새로고침부터 누른다.
  const { today, week, loading, reload } = useSafetyOverview({
    enabled: useNewLayout,
    refreshMs: 10000,
  });

  const [focusIndex, setFocusIndex] = useState(0);
  const [pickedKey, setPickedKey] = useState(""); // 아래 줄에서 고른 카메라(streamKey)
  const [shotRequest, setShotRequest] = useState(0); // 대기열 선택 → 캡처 보기 요청
  const newestIdRef = useRef("");

  const cameraList = useMemo(() => (Array.isArray(cameras) ? cameras : []), [cameras]);

  const allToday = useMemo(
    () => (Array.isArray(today?.violations) ? today.violations : []),
    [today]
  );

  // 미확인만, 최신순. 서버가 이미 최신순으로 주지만 화면에서 다시 보장한다.
  const openList = useMemo(
    () =>
      allToday
        .filter((v) => v?.status === "open")
        .sort((a, b) => String(b?.timestamp || "").localeCompare(String(a?.timestamp || ""))),
    [allToday]
  );

  const oldestMin = openList.length
    ? minutesSince(openList[openList.length - 1]?.timestamp)
    : null;

  /*
    새 위반이 들어오면 화면이 «알아서» 그 카메라로 넘어가야 한다.
    보던 자리를 지키면 정작 방금 터진 건을 못 본다 — 이 화면의 존재 이유가 그거다.
    카메라를 골라 보고 있었더라도 선택을 풀고 위반 화면으로 되돌린다.
  */
  const newestId = openList[0]?.id || "";
  useEffect(() => {
    if (!newestId) {
      newestIdRef.current = "";
      return;
    }
    if (newestIdRef.current && newestIdRef.current !== newestId) {
      setFocusIndex(0);
      setPickedKey("");
    }
    newestIdRef.current = newestId;
  }, [newestId]);

  // 카메라 한 대를 화면용 정보로 바꾼다(상태·배터리는 항상 최신값에서 다시 만든다).
  const toPicked = useCallback(
    (cam) => {
      if (!cam) return null;
      const key = String(cam?.streamKey || "").trim();
      if (!key) return null;

      const keys = [cam.camIdx, cam.id, cam.camNo, cam.cameraId, cam.streamKey]
        .map((v) => String(v ?? "").trim())
        .filter(Boolean);

      let meta = {};
      for (const k of keys) {
        if (statusMap[k]) {
          meta = statusMap[k] || {};
          break;
        }
      }

      const isOnline = String(meta?.liveStatus || "").toUpperCase() === "ONLINE";
      const bat = Number(meta?.battery);

      return {
        streamKey: key,
        webRtcUrl: String(cam?.webRtcUrl || "").trim(),
        name:
          String(meta?.workerName || "").trim() ||
          cam?.name ||
          cam?.camName ||
          cam?.camTitle ||
          key,
        sub:
          [meta?.branchName, meta?.teamName, meta?.vehicleNo]
            .map((v) => String(v || "").trim())
            .filter(Boolean)
            .join(" · ") || key,
        isOnline,
        battery:
          isOnline && Number.isFinite(bat) && bat > 0 && bat <= 100 ? bat : null,
      };
    },
    [statusMap]
  );

  const picked = useMemo(() => {
    const key = String(pickedKey || "").trim();
    if (!key) return null;
    return toPicked(cameraList.find((c) => String(c?.streamKey || "").trim() === key));
  }, [pickedKey, cameraList, toPicked]);

  /*
    위반이 없을 때 «영상이 있는데도 글자판»을 띄우지 않는다.
    관제 화면의 가장 큰 자리는 영상이 있으면 영상이 차지해야 한다.
    직접 고른 카메라가 없으면 송출 중인 첫 대를 자동으로 띄우고,
    송출이 한 대도 없을 때만 안전 현황판으로 내려간다.
  */
  const autoPicked = useMemo(() => {
    if (picked || openList.length > 0) return null;
    const online = cameraList.find((cam) => {
      const keys = [cam?.camIdx, cam?.id, cam?.camNo, cam?.cameraId, cam?.streamKey]
        .map((v) => String(v ?? "").trim())
        .filter(Boolean);
      for (const k of keys) {
        if (statusMap[k]) {
          return String(statusMap[k].liveStatus || "").toUpperCase() === "ONLINE";
        }
      }
      return false;
    });
    return toPicked(online);
  }, [picked, openList.length, cameraList, statusMap, toPicked]);

  const shownCamera = picked || autoPicked;

  if (!useNewLayout) {
    return <LegacyDashboard {...props} />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 pt-1">
      {/*
        A안 — 상단 지표 띠를 두지 않는다. 숫자는 각 카드 제목줄로 들어간다.
        (미확인 → 위반 카드 / 송출 중 → 카메라 줄 / 오늘·준수율 → 대기열 아래)
        띠를 없애니 가운데 영상이 그만큼 커진다.
      */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
        {/* 왼쪽 */}
        <div className="flex min-h-0 flex-col gap-4">
          {/*
            한 자리에 두 얼굴.
              · 미조치 위반이 있으면 → 위반 화면(가장 큰 자리를 경보가 차지)
              · 없으면            → 오늘의 안전 현황판(제일 큰 자리가 놀지 않게)
            둘이 동시에 뜨지 않으므로 평시에도 허전하지 않고, 위반이 묻히지도 않는다.
          */}
          {shownCamera || openList.length > 0 || (loading && !today) ? (
            <ViolationFocus
              violations={openList}
              index={focusIndex}
              onIndexChange={setFocusIndex}
              allToday={allToday}
              cameras={cameraList}
              statusMap={statusMap}
              loading={loading && !today}
              onChanged={reload}
              onNavigate={onNavigate}
              picked={shownCamera}
              autoPicked={!picked && !!autoPicked}
              onClearPick={() => setPickedKey("")}
              shotRequest={shotRequest}
            />
          ) : (
            <CalmBoard today={today} week={week} onNavigate={onNavigate} />
          )}

          <CameraStrip
            cameras={cameraList}
            statusMap={statusMap}
            onNavigate={onNavigate}
            pickedKey={pickedKey}
            onPick={(cam) =>
              setPickedKey((prev) => (prev === cam.streamKey ? "" : cam.streamKey))
            }
          />
        </div>

        {/* 오른쪽 — 오늘 작업 진행률 + 미확인 대기열 */}
        <div className="flex min-h-0 flex-col gap-4">
          {/* 위반이 있으면 작업 카드를 접어 대기열에 자리를 내준다. */}
          <WorkProgress enabled={useNewLayout} compact={openList.length > 0} />

          <OpenQueue
            openList={openList}
            activeIndex={focusIndex}
            onPick={(i) => {
              // 대기열에서 고른 건은 «그 순간»을 보려는 것이다 — 캡처로 전환한다.
              setFocusIndex(i);
              setPickedKey("");
              setShotRequest((v) => v + 1);
            }}
            onNavigate={onNavigate}
            oldestMin={oldestMin}
          />
        </div>
      </div>
    </div>
  );
}
